import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  TSmartScreenContextTip,
  useGetSmartScreenContextTips,
} from "../hooks/useGetSmartScreenContextTips";

type ProcessingTipsBannerProps = {
  visible: boolean;
  statusText?: string | null;
  progressPercent?: number;
};

const AUTOPLAY_MS = 6500;

const DocumentScanIllustration = () => (
  <svg
    width="96"
    height="96"
    viewBox="0 0 88 88"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="shrink-0 opacity-90"
  >
    <rect
      x="18"
      y="12"
      width="40"
      height="52"
      rx="4"
      fill="#DBEAFE"
      stroke="#3B82F6"
      strokeWidth="2"
    />
    <path
      d="M26 28h24M26 36h20M26 44h16"
      stroke="#60A5FA"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle
      cx="58"
      cy="58"
      r="14"
      fill="#EFF6FF"
      stroke="#2563EB"
      strokeWidth="2.5"
    />
    <circle cx="58" cy="58" r="6" stroke="#2563EB" strokeWidth="2" />
    <path
      d="M68 68l8 8"
      stroke="#2563EB"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const STATUS_COLOR_SCHEMES = [
  { bg: "from-blue-500 to-indigo-600", text: "text-white", dot: "bg-white", ping: "bg-white/50", sheen: "via-white/20", ring: "ring-blue-600/20" },
  { bg: "from-emerald-500 to-teal-600", text: "text-white", dot: "bg-white", ping: "bg-white/50", sheen: "via-white/20", ring: "ring-emerald-600/20" },
  { bg: "from-purple-500 to-fuchsia-600", text: "text-white", dot: "bg-white", ping: "bg-white/50", sheen: "via-white/20", ring: "ring-purple-600/20" },
  { bg: "from-orange-400 to-rose-500", text: "text-white", dot: "bg-white", ping: "bg-white/50", sheen: "via-white/20", ring: "ring-orange-600/20" },
  { bg: "from-pink-500 to-rose-600", text: "text-white", dot: "bg-white", ping: "bg-white/50", sheen: "via-white/20", ring: "ring-pink-600/20" },
  { bg: "from-lime-400 to-green-500", text: "text-green-950", dot: "bg-green-900", ping: "bg-green-900/50", sheen: "via-white/30", ring: "ring-lime-600/20" },
] as const;

const TIP_CARD_SCHEMES = [
  {
    bg: "bg-amber-50",
    border: "border-l-blue-500",
    label: "text-blue-600",
    title: "text-slate-900",
    subtitle: "text-slate-700",
    dot: "bg-blue-600",
    dotIdle: "bg-blue-200 hover:bg-blue-300",
    dotFill: "bg-white/50",
  },
  {
    bg: "bg-emerald-50",
    border: "border-l-emerald-500",
    label: "text-emerald-600",
    title: "text-slate-900",
    subtitle: "text-slate-700",
    dot: "bg-emerald-600",
    dotIdle: "bg-emerald-200 hover:bg-emerald-300",
    dotFill: "bg-white/50",
  },
  {
    bg: "bg-purple-50",
    border: "border-l-purple-500",
    label: "text-purple-600",
    title: "text-slate-900",
    subtitle: "text-slate-700",
    dot: "bg-purple-600",
    dotIdle: "bg-purple-200 hover:bg-purple-300",
    dotFill: "bg-white/50",
  },
  {
    bg: "bg-orange-50",
    border: "border-l-orange-500",
    label: "text-orange-600",
    title: "text-slate-900",
    subtitle: "text-slate-700",
    dot: "bg-orange-600",
    dotIdle: "bg-orange-200 hover:bg-orange-300",
    dotFill: "bg-white/50",
  },
  {
    bg: "bg-rose-50",
    border: "border-l-rose-500",
    label: "text-rose-600",
    title: "text-slate-900",
    subtitle: "text-slate-700",
    dot: "bg-rose-600",
    dotIdle: "bg-rose-200 hover:bg-rose-300",
    dotFill: "bg-white/50",
  },
  {
    bg: "bg-lime-50",
    border: "border-l-lime-500",
    label: "text-lime-700",
    title: "text-slate-900",
    subtitle: "text-slate-700",
    dot: "bg-lime-600",
    dotIdle: "bg-lime-200 hover:bg-lime-300",
    dotFill: "bg-white/50",
  },
] as const;

type StatusPillProps = {
  text: string;
  progress: number | null;
  colorIndex: number;
};

const StatusPill: React.FC<StatusPillProps> = ({
  text,
  progress,
  colorIndex,
}) => {
  const scheme = STATUS_COLOR_SCHEMES[colorIndex % STATUS_COLOR_SCHEMES.length];
  const trailing = progress != null ? ` · ${Math.round(progress)}%` : "";

  return (
    <div className="mt-4 flex items-center gap-3">
      <div
        className={`
          relative min-w-0 flex-1 overflow-hidden rounded-full
          bg-gradient-to-r ${scheme.bg}
          px-4 py-2 ring-1 ${scheme.ring}
          shadow-sm backdrop-blur-sm
          animate-[pillIn_450ms_cubic-bezier(0.22,1,0.36,1)]
        `}
      >
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 -translate-x-full animate-[sheen_3.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent ${scheme.sheen} to-transparent`}
        />

        <span className="absolute left-3 top-1/2 flex h-2 w-2 -translate-y-1/2">
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full ${scheme.ping} opacity-75`}
          />
          <span
            className={`relative inline-flex h-2 w-2 rounded-full ${scheme.dot}`}
          />
        </span>

        <div className="ml-5 overflow-hidden">
          <div
            className={`animate-[marquee_12s_linear_infinite] whitespace-nowrap text-sm font-semibold ${scheme.text}`}
          >
            <span>
              {text}
              {trailing}
            </span>
            <span className="mx-8 opacity-40">•</span>
            <span>
              {text}
              {trailing}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Keyframes: React.FC = () => (
  <style>{`
    @keyframes shimmer {
      0%   { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes marquee {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    @keyframes dotProgress {
      from { width: 0%; }
      to   { width: 100%; }
    }
    @keyframes pillIn {
      from { opacity: 0; transform: translateY(4px) scale(0.98); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes sheen {
      0%   { transform: translateX(-100%); }
      55%  { transform: translateX(100%); }
      100% { transform: translateX(100%); }
    }
    @media (prefers-reduced-motion: reduce) {
      .reduced-motion-safe [class*="animate-"] { animation: none !important; }
    }
  `}</style>
);


export const ProcessingTipsBanner: React.FC<ProcessingTipsBannerProps> = ({
  visible,
  statusText,
  progressPercent,
}) => {

  const { data: tips = [] } = useGetSmartScreenContextTips(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const [cycleKey, setCycleKey] = useState(0);

  const safeTips = useMemo(
    () => (tips.length > 0 ? tips : ([] as TSmartScreenContextTip[])),
    [tips],
  );

  const tipCount = safeTips.length;
  const activeTip = tipCount > 0 ? safeTips[activeIndex % tipCount] : null;

  const tipScheme = TIP_CARD_SCHEMES[activeIndex % TIP_CARD_SCHEMES.length];

  useEffect(() => {
    if (tipCount <= 0) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex(Math.floor(Math.random() * tipCount));
  }, [tipCount]);

  useEffect(() => {
    if (!visible || paused || tipCount <= 1) return;

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const id = window.setInterval(() => {
      setActiveIndex((current) => {
        let next = Math.floor(Math.random() * tipCount);
        if (tipCount > 1) {
          while (next === current) next = Math.floor(Math.random() * tipCount);
        }
        return next;
      });
    }, AUTOPLAY_MS);

    return () => window.clearInterval(id);
  }, [visible, paused, tipCount, cycleKey]);

  const goTo = useCallback(
    (index: number) => {
      if (tipCount === 0) return;
      setActiveIndex(((index % tipCount) + tipCount) % tipCount);
      setCycleKey((k) => k + 1);
    },
    [tipCount],
  );

  const [statusColorIndex, setStatusColorIndex] = useState(0);
  const prevStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (statusText === prevStatusRef.current) return;
    prevStatusRef.current = statusText ?? null;
    if (statusText) setStatusColorIndex((i) => i + 1);
  }, [statusText]);

  if (!visible || !activeTip) return null;

  const clampedProgress =
    typeof progressPercent === "number"
      ? Math.max(0, Math.min(100, progressPercent))
      : null;

  return (
    <>
      <Keyframes />
      <section
        className="
  group relative w-full overflow-hidden rounded-3xl border border-sky-100
  bg-[linear-gradient(135deg,#ffffff_0%,#f0f9ff_50%,#eef2ff_100%)]
  px-6 py-6 shadow-sm
  ring-1 ring-white reduced-motion-safe
"
        aria-live="polite"
        aria-label="Smart Screen processing tips"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 -top-16 h-48 w-48 rounded-full bg-blue-300/30 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-indigo-300/30 blur-3xl"
        />

        <div className="relative flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-4">
              <span
                className="mt-0.5 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-md ring-1 ring-blue-100"
                aria-hidden="true"
              >
                <span className="h-6 w-6 animate-spin rounded-full border-[3px] border-blue-200 border-t-blue-600" />
              </span>
              <div className="min-w-0">
                <h2 className="bg-gradient-to-r from-blue-700 via-indigo-600 to-sky-500 bg-[length:200%_auto] bg-clip-text text-lg font-extrabold tracking-tight text-transparent sm:text-xl animate-[shimmer_3s_linear_infinite]">
                  We are working on the CVs...
                </h2>
                <p className="mt-1 text-sm text-slate-600 sm:text-base">
                  This may take a few minutes — you can keep using Arno in the
                  meantime.
                </p>
              </div>
            </div>

            {statusText ? (
              <StatusPill
                key={statusColorIndex}
                text={statusText}
                progress={clampedProgress}
                colorIndex={statusColorIndex}
              />
            ) : null}

            <div
              key={`tip-card-${activeIndex}`}
              className={`
    mt-6 rounded-r-2xl rounded-l-md border border-slate-200 border-l-4
    ${tipScheme.bg} ${tipScheme.border}
    p-5 shadow-sm
    animate-[fadeUp_450ms_ease-out]
  `}
            >
              <div className="flex items-start gap-3">
                {activeTip.icon_url ? (
                  <img
                    src={activeTip.icon_url}
                    alt=""
                    className="mt-0.5 h-9 w-9 shrink-0 object-contain"
                  />
                ) : (
                  <span
                    className="mt-0.5 text-xl leading-none"
                    aria-hidden="true"
                  >
                    ✨
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-[11px] font-bold uppercase tracking-[0.14em] ${tipScheme.label}`}
                  >
                    Did you know?
                  </p>
                  <p
                    key={`title-${activeTip.id}-${activeIndex}`}
                    className={`mt-1 animate-[fadeUp_400ms_ease-out] text-base font-semibold sm:text-lg ${tipScheme.title}`}
                  >
                    {activeTip.title}
                  </p>
                  {activeTip.subtitle ? (
                    <p
                      key={`sub-${activeTip.id}-${activeIndex}`}
                      className={`mt-1 animate-[fadeUp_500ms_ease-out] text-sm ${tipScheme.subtitle}`}
                    >
                      {activeTip.subtitle}
                    </p>
                  ) : null}
                </div>
              </div>

              {tipCount > 1 ? (
                <div
                  className="mt-4 flex items-center justify-center gap-2"
                  role="tablist"
                  aria-label="Tip slides"
                >
                  {safeTips.map((tip, index) => {
                    const isActive = index === activeIndex;
                    return (
                      <button
                        key={tip.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-label={`Show tip ${index + 1} of ${tipCount}`}
                        onClick={() => goTo(index)}
                        className={`relative h-2.5 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                          isActive
                            ? `w-8 ${tipScheme.dot}`
                            : `w-2.5 ${tipScheme.dotIdle}`
                        }`}
                      >
                        {isActive && !paused ? (
                          <span
                            key={cycleKey}
                            className="pointer-events-none absolute inset-0 overflow-hidden rounded-full"
                          >
                            <span
                              className={`block h-full w-0 rounded-full ${tipScheme.dotFill} animate-[dotProgress_var(--duration)_linear_forwards]`}
                              style={
                                {
                                  "--duration": `${AUTOPLAY_MS}ms`,
                                } as React.CSSProperties
                              }
                            />
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>

          <div className="hidden items-center justify-center md:flex md:pl-4">
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 animate-pulse rounded-full bg-blue-200/40 blur-2xl"
              />
              <DocumentScanIllustration />
            </div>
          </div>
        </div>

        {clampedProgress != null ? (
          <div className="relative mt-5 h-1.5 w-full overflow-hidden rounded-full bg-white/80">
            <div
              className="h-full rounded-full bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-500 transition-all duration-500"
              style={{ width: `${clampedProgress}%` }}
            />
          </div>
        ) : null}
      </section>
    </>
  );
};

export default ProcessingTipsBanner;
